import React, { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { AddAnimalModal, AddAnimalToPen, MoveAnimalToPen, ProgressBar } from "../.."
import { colors, fonts, glyphs, } from "../../../Styles"
import { LfLocation } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall';
import MoveAnimalModal from '../../OperationsComponents/Modals/moveAnimalModal';
import MoveAnimalFromPenModal from '../Modals/moveAnimalFromPenModal';
import { PanelMaintainAnimal } from '../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall';
import { useAppSelector } from '../../../Store';
import { keyBy, merge } from 'lodash';
import { useSection } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections';
import { Graph_QL } from '../../../Services';

interface PenMove_Props {
  pen?: LfLocation; 
  hasHoverEffect?: boolean
  dirtyDataRef: MutableRefObject<PanelMaintainAnimal[]>
}
/**
 * PEN_ADD
 * @param penId Unique identifier for each pen.
 * @param hasHoverEffect Boolean that, if true, adds a hover effect to the pen.
 * @returns a pen where onMouseEnter adds a hoverEffect on empty pens.
 */
export default function PEN_MOVE({ pen, hasHoverEffect, dirtyDataRef }: PenMove_Props) {
  const {t} = useTranslation()
  const [isHovered, setIsHovered] = useState(false);
  const [showMoveAnimalFromPenModal, setMoveAnimalFromPenModal] = useState(false)
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>();

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);
  
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  
  useEffect(() => {
    getLfSectionsInfo({fetchAmount: 50});
    // eslint-disable-next-line
  }, []);

  // #region | Data handling
  // New Location
  const newLocationName = useMemo(() => {
    if (!selectedLocationId) return undefined;
    return lfSectionsState[domain]?.data
      ?.flatMap(section => section.section_locations)
      ?.find(loc => loc.location_id === selectedLocationId)?.name;
  }, [domain, lfSectionsState, selectedLocationId]);

  // Animalinfo if animal still excists in pen
  const animalInfo = useMemo(() => {
    const animal = pen?.animals && pen.animals.length > 0 ? pen.animals[0] : undefined
    const currentAnimal = dirtyDataRef.current.find(a => a.animalId === animal?.animal_id);
    
    // Feed Correction
    const feedCorrection = animal
    ? animal.correction === 0 ||
      animal.correction === undefined ||
      animal.correction === null
      ? "0%"
      : animal.correction + "%"
    : "0%";
    
    // // Feed Skip TODO
    // const defaultFeeding = true // Untill data excists
    // const feedSkip = defaultFeeding ? t("on", {defaultValue:"on"}) : t("off",{defaultValue:"off"});
    // const feedSkipStyle: CSSProperties = {
    //   color: defaultFeeding ? colors.systemGreen : colors.systemRed
    // };
  
    // Milestone
    const milestoneDayNo = animal?.milestone_day
      ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24))
      : t("noData", { defaultValue: "no data" });
  
    const milestoneName = animal?.milestone?.name ?? "";
  
    if (animal) {
      dirtyDataRef.current = Object.values(
        merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
          ...currentAnimal,
          animalId: animal?.animal_id,
          location: animal?.location_id,
        }], "animalId"))
      );
    }

    return {
      feedCorrection,
      milestoneDayNo,
      milestoneName,
      animal
    };
  }, [dirtyDataRef, pen?.animals, t]);

  // ConsumptionProgressBar
  const ConsumptionProgressBar: JSX.Element = useMemo(() => {
    const progress = (animalInfo.animal?.consumptions?.[0])
    const progressBarTotal = (progress?.total ?? 0) / 1000;
    const progressBarProgressed = (progress?.progressed ?? 0) / 1000;

    return (
      <ProgressBar
        total={+(progressBarTotal).toFixed(2)}
        progressed={+(progressBarTotal - progressBarProgressed).toFixed(2)}
      />
    );
  }, [animalInfo.animal?.consumptions])

  // Update animal
  useEffect(() => {
    if (animalInfo.animal?.animal_id) {
      const current = dirtyDataRef.current.find(a => a.animalId === animalInfo.animal?.animal_id);
      const updatedAnimal: PanelMaintainAnimal = {
        ...(current ?? animalInfo.animal),
        animalId: animalInfo.animal.animal_id,
        location: selectedLocationId,
      };
  
      dirtyDataRef.current = Object.values(
        merge(
          {},
          keyBy(dirtyDataRef.current, "animalId"),
          keyBy([updatedAnimal], "animalId")
        )
      );
    }
  }, [selectedLocationId, animalInfo.animal, dirtyDataRef]);
  // #endregion

  // #region | Content
  // Advanced content to parse in the div
  const advancedContent =
    <>
      { animalInfo.animal && selectedLocationId !== undefined ? 
        <div style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          <span></span>
            <span style={{}}>{pen?.name}</span>
            <span style={{display: "flex", alignItems: "center", gap: "10px"}}>Animal {animalInfo.animal.animal_number}<glyphs.Arrow fillColor={colors.greyDarkPrimary}/>{selectedLocationId === null ?  `Pen ${newLocationName}` : `Undefined`}</span>
          <span></span>
        </div>
      : animalInfo.animal ?
        <div style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "22px 45px 45px 108px 45px 108px 45px 100px 45px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          <span></span>
          <span style={{}}>{pen?.name}</span>
          <span style={{}}>{animalInfo.animal.animal_number}</span>
          <span style={{}}>{animalInfo.milestoneName}</span>
          <span style={{}}>{animalInfo.milestoneDayNo}</span>
          <span style={{paddingLeft: "5px"}}>{animalInfo.animal.feedCurve?.name}</span>
          <span style={{paddingLeft: "5px"}}>{animalInfo.feedCorrection}</span>
          <span style={{}}> {ConsumptionProgressBar} </span>
          {/* <span style={{paddingLeft: "5px", ...feedSkipStyle}}>{feedSkip}</span> */}
          <span></span>
        </div>
      : <div style={{ cursor: "default", display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          <span></span>
          <span style={{filter: "opacity(0.5)"}}>{pen?.name}</span>
          <span style={{filter: "opacity(0.5)"}}>{t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}</span>
          <span></span>
        </div>
      }
    </>
  // #endregion
  
  return (
    <>
      <div 
        onClick={animalInfo.animal ? () => setMoveAnimalFromPenModal(true) : undefined}
        onMouseEnter={(hasHoverEffect && animalInfo.animal) ? () => setIsHovered(true) : undefined}
        onMouseLeave={(hasHoverEffect && animalInfo.animal) ? () => setIsHovered(false) : undefined}
        style={{ 
          width: "650px",
          height: "35px",
          cursor: hasHoverEffect && animalInfo.animal? "pointer" : "", 
          boxSizing: "border-box"
      }}>
        <div style={{
          width: "100%",
          height: "100%",
          textAlign: 'center',
          alignContent: "center",
          boxSizing: "border-box",
          padding: "0px 10px",
          backgroundColor: isHovered ? colors.greyLightPrimary : colors.greyLightSecondary,
          transition: 'filter 0.3s',
          color: colors.greyDarkPrimary,
          ...fonts.paragraph,
          ...fonts.fontSize4,
        }}>
          {advancedContent}
        </div>
      </div>
      {/* Add Animal Modal */}
      {animalInfo.animal ? <MoveAnimalFromPenModal
        activeAnimal={animalInfo.animal}
        isModalOpen={showMoveAnimalFromPenModal}
        goBack={() => setMoveAnimalFromPenModal(false)}
        activePen={pen}
        onChange={(id) => {
          setSelectedLocationId(id);
        }}
      /> : <></> }
      
    </>
  );
}